/**
 * ScrollReveal (по мотивам React Bits): блок проявляется и поднимается,
 * когда попадает в область видимости, — один раз, с задержкой по `index`
 * для «лесенки». Пока блок на экране, он ещё слегка отстаёт от прокрутки
 * (`useScroll` + `useTransform`): соседние ряды движутся с разной
 * скоростью — лёгкий параллакс глубины.
 */

import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { useRef, type ReactNode } from 'react';

interface ScrollRevealProps {
  readonly children: ReactNode;
  readonly className?: string;
  /** Порядковый номер в группе: задержка 70 мс на шаг. */
  readonly index?: number;
  /** Сила параллакса при прокрутке, px; 0 — без него. */
  readonly depth?: number;
}

const STAGGER = 0.07;

export function ScrollReveal({ children, className, index = 0, depth = 16 }: ScrollRevealProps) {
  const reduceMotion = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const drift = useTransform(scrollYProgress, [0, 1], [depth, -depth]);

  return (
    <motion.div ref={ref} className={className} style={reduceMotion === true ? undefined : { y: drift }}>
      <motion.div
        className="h-full"
        initial={reduceMotion === true ? false : { opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ delay: index * STAGGER, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
